import React, { useState, useRef, useEffect } from 'react';
import { 
  Send,
  Trash2,
  Terminal,
  History,
  HardDrive
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const namespaces = [
  'default',
  'kube-system',
  'kube-public',
  'monitoring',
  'ingress-nginx',
];

const suggestedCommands = [
  'kubectl get pods',
  'kubectl get nodes',
  'kubectl get services',
  'kubectl get deployments',
  'kubectl describe pod [pod-name]',
  'kubectl logs [pod-name]',
  'kubectl exec -it [pod-name] -- /bin/bash',
  'kubectl apply -f [filename]'
];

// Simulated command history - this would come from the user profile in a real app
const commandHistory = [
  'kubectl get pods',
  'kubectl describe pod nginx-deployment-54f57cf6bf-9pnxh',
  'kubectl get nodes',
  'kubectl get services',
  'kubectl logs nginx-deployment-54f57cf6bf-9pnxh'
];

export default function LiveTerminal({
  user,
  clusters = [],
  activeCluster,
  activeNamespace,
  terminalConnected,
  terminalOutput = [],
  setActiveCluster,
  setActiveNamespace,
  connectTerminal,
  disconnectTerminal,
  executeCommand,
  clearTerminalOutput,
  addTerminalOutput
}) {
  // Extract stable user properties to prevent infinite loops
  const userId = user?.user?._id;
  const userToken = user?.token;

  const [selectedCluster, setSelectedCluster] = useState(activeCluster || '');
  const [selectedNamespace, setSelectedNamespace] = useState(activeNamespace || 'default');
  const [command, setCommand] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [historyMenuOpen, setHistoryMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const terminalRef = useRef(null);

  // Sync local state with props when they change
  useEffect(() => {
    if (activeCluster && activeCluster !== selectedCluster) {
      setSelectedCluster(activeCluster);
    }
  }, [activeCluster, selectedCluster]);

  useEffect(() => {
    if (activeNamespace && activeNamespace !== selectedNamespace) {
      setSelectedNamespace(activeNamespace);
    }
  }, [activeNamespace, selectedNamespace]);

  // Connect to terminal when cluster is selected
  useEffect(() => {
    if (selectedCluster && userId && userToken && !terminalConnected) {
      setIsLoading(true);
      connectTerminal(userId, userToken, selectedCluster)
        .then(() => setIsLoading(false))
        .catch(() => setIsLoading(false));
    }
  }, [selectedCluster, userId, userToken, connectTerminal, terminalConnected]);

  // Always scroll to bottom when terminal output changes
  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [terminalOutput]);

  const handleClusterChange = (value) => {
    setSelectedCluster(value);
    
    // Update the global active cluster
    if (setActiveCluster) {
      setActiveCluster(value);
    }
    
    // Disconnect from current terminal if connected
    if (terminalConnected) {
      disconnectTerminal();
      clearTerminalOutput();
    }
  };
  
  const handleNamespaceChange = (value) => {
    setSelectedNamespace(value);
    
    // Update the global active namespace
    if (setActiveNamespace) {
      setActiveNamespace(value);
    }
    
    if (terminalConnected && userId && userToken) {
      // Execute namespace change command
      executeCommand(
        userId,
        userToken,
        selectedCluster,
        `kubectl config set-context --current --namespace=${value}`
      );
    }
  };
  
  const handleCommandChange = (event) => {
    setCommand(event.target.value);
  };
  
  const handleCommandSubmit = () => {
    if (!command.trim() || !userId || !userToken) return;
    
    if (!terminalConnected) {
      addTerminalOutput({
        type: 'error',
        content: 'Not connected to a cluster. Please select a cluster first.'
      });
      return;
    }
    
    // Add command to terminal output
    addTerminalOutput({
      type: 'command',
      content: command
    });
    
    // Handle special commands
    if (command === 'clear') {
      clearTerminalOutput();
      setCommand('');
      return;
    }
    
    if (command === 'help') {
      addTerminalOutput({
        type: 'output', 
        content: 'Available commands:\n' +
                 '  clear - Clear the terminal\n' +
                 '  help - Show this help message\n' +
                 'You can use any kubectl command directly.'
      });
      setCommand('');
      return;
    }
    
    // Execute kubectl commands through Redux action
    if (command.startsWith('kubectl')) {
      executeCommand(userId, userToken, selectedCluster, command);
    } else {
      // Unknown command
      addTerminalOutput({
        type: 'error',
        content: `Command not found: ${command}`
      });
    }
    
    setCommand('');
  };
  
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleCommandSubmit();
    } else if (event.key === 'Tab') {
      event.preventDefault();
      setShowSuggestions(true);
    }
  };
  
  const handleSuggestionClick = (suggestion) => {
    setCommand(suggestion);
    setShowSuggestions(false);
  };
  
  const handleClearTerminal = () => {
    clearTerminalOutput();
  };
  
  const handleHistoryItemClick = (historyCommand) => {
    setCommand(historyCommand);
    setHistoryMenuOpen(false);
  };

  return (
    <div className="px-6">
      <h2 className="text-lg font-medium mb-4">
        Live Terminal / Shell Access
      </h2>
      
      <Card className="p-6 mb-6 rounded-xl border border-gray-200">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cluster</label>
            <Select value={selectedCluster} onValueChange={handleClusterChange} disabled={isLoading}>
              <SelectTrigger>
                <SelectValue placeholder="Select Cluster" />
              </SelectTrigger>
              <SelectContent>
                {clusters.map(cluster => (
                  <SelectItem key={cluster.id} value={cluster.id}>
                    <div className="flex items-center gap-2">
                      <HardDrive className="w-4 h-4 text-blue-600" />
                      {cluster.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Namespace</label>
            <Select 
              value={selectedNamespace} 
              onValueChange={handleNamespaceChange}
              disabled={!terminalConnected || isLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Namespace" />
              </SelectTrigger>
              <SelectContent>
                {namespaces.map(namespace => (
                  <SelectItem key={namespace} value={namespace}>
                    <div className="flex items-center gap-2">
                      <Terminal className="w-4 h-4 text-blue-600" />
                      {namespace}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>
    </div>
  );
} 